import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Plus, X, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MultiValueEntry } from "@/types/fieldRegistry";

// Editor de lista de valores rotulados (telefone, e-mail). Era uma função privada dentro de
// FieldRenderer.tsx, usada só pelos campos personalizados; foi extraída para poder servir
// também ao telefone do lead, que é COLUNA REAL (regra 10 do AGENTS.md) e por isso nunca
// passou pelo FieldRenderer.
//
// As props novas são todas opcionais e default-off: com `showPrimary` desligado e sem `mask`,
// o comportamento é idêntico ao de antes para quem já usava.

interface MultiValueRendererProps {
  value: MultiValueEntry[] | string | null;
  onChange: (v: MultiValueEntry[]) => void;
  labels: string[];
  inputType: string;
  placeholder: string;
  disabled?: boolean;
  /** Exibe o botão de eleger o principal. */
  showPrimary?: boolean;
  /** Formata o valor exibido (ex.: máscara de telefone). O valor guardado continua cru. */
  mask?: (v: string) => string;
  /** Devolve a mensagem de erro da entrada, ou null quando válida. */
  validate?: (v: string) => string | null;
}

export function MultiValueRenderer({
  value,
  onChange,
  labels,
  inputType,
  placeholder,
  disabled,
  showPrimary = false,
  mask,
  validate,
}: MultiValueRendererProps) {
  // Compatibilidade: se valor é string simples, converter para array
  const entries: MultiValueEntry[] = Array.isArray(value)
    ? value
    : value
      ? [{ value: String(value), label: labels[0] || '', is_primary: true }]
      : [];

  const addEntry = () => {
    onChange([
      ...entries,
      { value: '', label: labels[0] || '', is_primary: entries.length === 0 },
    ]);
  };

  const updateEntry = (index: number, updates: Partial<MultiValueEntry>) => {
    const updated = entries.map((entry, i) =>
      i === index ? { ...entry, ...updates } : entry
    );
    onChange(updated);
  };

  const removeEntry = (index: number) => {
    const updated = entries.filter((_, i) => i !== index);
    // Se removemos o primary, marcar o primeiro como primary
    if (updated.length > 0 && !updated.some((e) => e.is_primary)) {
      updated[0].is_primary = true;
    }
    onChange(updated);
  };

  const makePrimary = (index: number) => {
    onChange(entries.map((entry, i) => ({ ...entry, is_primary: i === index })));
  };

  return (
    <div className="space-y-2">
      {entries.map((entry, index) => {
        const error = validate && entry.value ? validate(entry.value) : null;
        return (
          <div key={index} className="space-y-1">
            {/* `flex-wrap` + base mínima no input: em container estreito o rótulo/remover descem
                para a segunda linha, em vez de o input encolher até virar um risco de 30px. */}
            <div className="flex flex-wrap items-center gap-2">
              {showPrimary && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  disabled={disabled}
                  onClick={() => makePrimary(index)}
                  aria-label={entry.is_primary ? 'Telefone principal' : 'Marcar como principal'}
                  title={entry.is_primary ? 'Principal' : 'Marcar como principal'}
                >
                  <Star
                    className={cn(
                      'h-4 w-4',
                      entry.is_primary
                        ? 'fill-amber-400 text-amber-500'
                        : 'text-muted-foreground',
                    )}
                  />
                </Button>
              )}
              <Input
                type={inputType}
                value={mask ? mask(entry.value) : entry.value}
                onChange={(e) => updateEntry(index, { value: e.target.value })}
                placeholder={placeholder}
                disabled={disabled}
                className={cn('w-auto min-w-[9rem] flex-1', error && 'border-destructive')}
                aria-invalid={error ? true : undefined}
              />
              <div className="ml-auto flex shrink-0 items-center gap-2">
                <SearchableSelect
                  value={entry.label}
                  onValueChange={(label) => updateEntry(index, { label })}
                  disabled={disabled}
                  options={labels.map(label => ({ value: label, label }))}
                  triggerClassName="w-[108px] sm:w-[128px]"
                />
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => removeEntry(index)}
                    aria-label={`Remover ${placeholder.toLowerCase()}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {error && <p className="text-xs text-destructive pl-1">{error}</p>}
          </div>
        );
      })}
      {!disabled && (
        <Button type="button" variant="outline" size="sm" onClick={addEntry} className="w-full">
          <Plus className="h-4 w-4 mr-1" /> Adicionar {placeholder.toLowerCase()}
        </Button>
      )}
    </div>
  );
}
